/**
 * Agent Config Editor Component
 *
 * Full workspace .md file editor with:
 * - File browser with descriptions
 * - Inline editor with live markdown preview
 * - Conflict analyzer across files
 * - Version history with diff view, revert, and "known good" marking
 * - Save to agent workspace via API
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";
import "./quality-dashboard.js";

interface WorkspaceFile {
  name: string;
  exists: boolean;
  size?: number;
  error?: string;
  description: string;
  icon: string;
  archived?: boolean;
}

interface Conflict {
  id: string;
  severity: "warning" | "error";
  message: string;
  fileA: string;
  lineA?: number;
  snippetA: string;
  fileB: string;
  lineB?: number;
  snippetB: string;
}

interface FileVersion {
  id: string;
  filename: string;
  version_number: number;
  is_known_good: boolean;
  created_at: string;
  created_by: string;
  content_length: number;
}

interface FileVersionDetail {
  id: string;
  filename: string;
  content: string;
  previous_content: string | null;
  version_number: number;
  is_known_good: boolean;
  created_at: string;
  created_by: string;
}

const FILE_DESCRIPTIONS: Record<string, { description: string; icon: string }> = {
  "SOUL.md": { description: "Core personality, tone, values, and behavior rules", icon: "🧬" },
  "AGENTS.md": { description: "Workspace conventions, memory rules, safety policies", icon: "📋" },
  "IDENTITY.md": { description: "Name, persona, and how the agent presents itself", icon: "🎭" },
  "USER.md": { description: "Info about you — preferences, context, relationship", icon: "👤" },
  "HEARTBEAT.md": { description: "Periodic check-in tasks and proactive behaviors", icon: "💓" },
  "BOOTSTRAP.md": {
    description: "First-run instructions (deleted after initial setup)",
    icon: "🚀",
  },
  "TOOLS.md": { description: "Tool-specific notes, API keys, local config", icon: "🔧" },
  "MEMORY.md": { description: "Long-term curated memories and lessons learned", icon: "🧠" },
};

// Patterns that may conflict across files
const CONFLICT_PATTERNS = [
  { regex: /\b(formal|professional|corporate)\b/i, category: "tone", label: "formal tone" },
  {
    regex: /\b(casual|friendly|chill|relaxed|informal)\b/i,
    category: "tone",
    label: "casual tone",
  },
  {
    regex: /\b(verbose|detailed|thorough|comprehensive)\b/i,
    category: "verbosity",
    label: "verbose output",
  },
  {
    regex: /\b(concise|brief|terse|short|minimal)\b/i,
    category: "verbosity",
    label: "concise output",
  },
  {
    regex: /\b(never|don't|do not|must not|forbidden)\s+(use|run|execute|call)\b/i,
    category: "restriction",
    label: "tool restriction",
  },
  {
    regex: /\b(always|must|should always)\s+(use|run|execute|call)\b/i,
    category: "restriction",
    label: "tool mandate",
  },
  {
    regex: /\b(ask first|ask before|confirm before|get permission)\b/i,
    category: "autonomy",
    label: "ask before acting",
  },
  {
    regex: /\b(proceed autonomously|act freely|don't ask|without asking)\b/i,
    category: "autonomy",
    label: "act autonomously",
  },
  { regex: /\b(emoji|emojis|emoticons)\b/i, category: "emoji", label: "emoji usage" },
  { regex: /\b(no emoji|avoid emoji|skip emoji)\b/i, category: "emoji", label: "no emoji" },
];

@customElement("rax-agent-config-editor")
export class RaxAgentConfigEditor extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
        height: 100%;
      }
      
      /* Mode tabs - switch between Config Files and Learned Preferences */
      .mode-tabs {
        display: flex;
        gap: var(--space-1);
        padding: var(--space-3) var(--space-4);
        background: var(--bg-secondary);
        border-bottom: 1px solid var(--border-color);
      }
      
      .mode-tab {
        padding: var(--space-2) var(--space-4);
        background: transparent;
        border: 1px solid transparent;
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--font-sm);
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .mode-tab:hover {
        color: var(--text-primary);
        background: var(--bg-elevated);
      }
      
      .mode-tab.active {
        color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.1);
        border-color: rgba(255, 208, 0, 0.3);
      }
      
      .editor-container {
        display: flex;
        height: calc(100vh - 120px);
        overflow: hidden;
      }
      
      /* Sidebar file browser */
      .file-sidebar {
        width: 280px;
        min-width: 280px;
        border-right: 1px solid var(--border-color);
        display: flex;
        flex-direction: column;
        background: var(--bg-secondary);
      }
      
      .sidebar-header {
        padding: var(--space-4) var(--space-4) var(--space-3);
        border-bottom: 1px solid var(--border-color);
      }
      
      .sidebar-title {
        font-size: var(--font-base);
        font-weight: 700;
        color: var(--rax-yellow);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .file-list {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-2);
      }
      
      .file-item {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        padding: var(--space-3);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        border: 1px solid transparent;
        margin-bottom: var(--space-1);
      }
      
      .file-item:hover {
        background: var(--bg-hover);
        border-color: var(--border-color);
      }
      
      .file-item.active {
        background: rgba(255, 208, 0, 0.1);
        border-color: var(--rax-yellow);
      }
      
      .file-item.not-exists {
        opacity: 0.5;
      }
      
      .file-icon {
        font-size: 20px;
        flex-shrink: 0;
        margin-top: 2px;
      }
      
      .file-info {
        flex: 1;
        min-width: 0;
      }
      
      .file-name {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-primary);
        font-family: ui-monospace, monospace;
      }
      
      .file-desc {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        margin-top: 2px;
        line-height: 1.3;
      }
      
      .file-status {
        font-size: 10px;
        padding: 1px 6px;
        border-radius: var(--radius-full);
        font-weight: 600;
        flex-shrink: 0;
        margin-top: 3px;
      }
      
      .file-status.exists {
        background: rgba(16, 185, 129, 0.2);
        color: var(--success);
      }
      
      .file-status.missing {
        background: rgba(239, 68, 68, 0.15);
        color: var(--text-tertiary);
      }
      
      /* Tab bar for editor/conflicts/history */
      .tab-bar {
        display: flex;
        border-bottom: 1px solid var(--border-color);
        background: var(--bg-secondary);
      }
      
      .tab {
        padding: var(--space-3) var(--space-4);
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-secondary);
        cursor: pointer;
        border-bottom: 2px solid transparent;
        transition: all var(--transition-fast);
        background: none;
        border-top: none;
        border-left: none;
        border-right: none;
      }
      
      .tab:hover {
        color: var(--text-primary);
      }
      
      .tab.active {
        color: var(--rax-yellow);
        border-bottom-color: var(--rax-yellow);
      }
      
      .tab .badge {
        display: inline-block;
        background: var(--error);
        color: white;
        font-size: 10px;
        padding: 1px 6px;
        border-radius: var(--radius-full);
        margin-left: var(--space-2);
        font-weight: 700;
      }
      
      .tab .badge-muted {
        display: inline-block;
        background: rgba(255, 208, 0, 0.2);
        color: var(--rax-yellow);
        font-size: 10px;
        padding: 1px 6px;
        border-radius: var(--radius-full);
        margin-left: var(--space-2);
        font-weight: 700;
      }
      
      /* Main content area */
      .main-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;
        overflow: hidden;
      }
      
      /* Editor area */
      .editor-area {
        flex: 1;
        display: flex;
        overflow: hidden;
      }
      
      .editor-pane {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      
      .editor-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-2) var(--space-4);
        background: var(--bg-elevated);
        border-bottom: 1px solid var(--border-color);
      }
      
      .file-warning {
        margin: 0 var(--space-4) var(--space-2);
        padding: var(--space-2) var(--space-3);
        border: 1px solid rgba(245, 158, 11, 0.4);
        border-radius: var(--radius-md);
        background: rgba(245, 158, 11, 0.12);
        color: var(--warning);
        font-size: var(--font-xs);
        line-height: 1.4;
      }
      
      .editor-filename {
        font-family: ui-monospace, monospace;
        font-size: var(--font-sm);
        color: var(--text-primary);
        font-weight: 600;
      }
      
      .editor-actions {
        display: flex;
        gap: var(--space-2);
        align-items: center;
      }
      
      .save-indicator {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .save-indicator.unsaved {
        color: var(--warning);
      }
      
      .save-indicator.saving {
        color: var(--rax-yellow);
      }
      
      .save-indicator.saved {
        color: var(--success);
      }
      
      .btn {
        padding: var(--space-2) var(--space-3);
        border: none;
        border-radius: var(--radius-md);
        font-size: var(--font-xs);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .btn-primary {
        background: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .btn-primary:hover {
        background: var(--rax-gold);
      }
      
      .btn-primary:disabled {
        opacity: 0.5;
        cursor: default;
      }
      
      .btn-secondary {
        background: var(--bg-elevated);
        color: var(--text-secondary);
        border: 1px solid var(--border-color);
      }
      
      .btn-secondary:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .btn-sm {
        padding: 3px 8px;
        font-size: 11px;
      }
      
      .btn-danger {
        background: rgba(239, 68, 68, 0.15);
        color: var(--error);
        border: 1px solid rgba(239, 68, 68, 0.3);
      }
      
      .btn-danger:hover {
        background: rgba(239, 68, 68, 0.25);
      }
      
      .btn-star {
        background: rgba(255, 208, 0, 0.1);
        color: var(--rax-yellow);
        border: 1px solid rgba(255, 208, 0, 0.3);
      }
      
      .btn-star:hover {
        background: rgba(255, 208, 0, 0.2);
      }
      
      .code-editor {
        flex: 1;
        width: 100%;
        padding: var(--space-4);
        background: var(--bg-primary);
        color: var(--text-primary);
        border: none;
        font-family: ui-monospace, "SF Mono", "Cascadia Code", monospace;
        font-size: 13px;
        line-height: 1.6;
        resize: none;
        outline: none;
        tab-size: 2;
        overflow-y: auto;
        box-sizing: border-box;
      }
      
      .code-editor::placeholder {
        color: var(--text-tertiary);
      }
      
      /* Preview pane */
      .preview-pane {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-4) var(--space-5);
        background: var(--bg-primary);
        border-left: 1px solid var(--border-color);
        min-width: 0;
      }
      
      .preview-pane h1,
      .preview-pane h2,
      .preview-pane h3 {
        color: var(--rax-yellow);
        margin: var(--space-4) 0 var(--space-2);
      }
      
      .preview-pane h1 {
        font-size: var(--font-xl);
        border-bottom: 1px solid var(--border-color);
        padding-bottom: var(--space-2);
      }
      .preview-pane h2 {
        font-size: var(--font-lg);
      }
      .preview-pane h3 {
        font-size: var(--font-base);
      }
      
      .preview-pane p {
        color: var(--text-secondary);
        margin: var(--space-2) 0;
        line-height: 1.6;
      }
      .preview-pane ul,
      .preview-pane ol {
        color: var(--text-secondary);
        padding-left: var(--space-5);
      }
      .preview-pane li {
        margin: var(--space-1) 0;
      }
      .preview-pane code {
        background: var(--bg-elevated);
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        font-family: ui-monospace, monospace;
        font-size: 0.9em;
        color: var(--rax-gold);
      }
      .preview-pane pre {
        background: var(--bg-elevated);
        padding: var(--space-3);
        border-radius: var(--radius-md);
        overflow-x: auto;
        border: 1px solid var(--border-color);
      }
      .preview-pane pre code {
        background: none;
        padding: 0;
      }
      .preview-pane blockquote {
        border-left: 3px solid var(--rax-yellow);
        padding-left: var(--space-3);
        color: var(--text-tertiary);
        margin: var(--space-3) 0;
      }
      .preview-pane strong {
        color: var(--text-primary);
      }
      .preview-pane hr {
        border: none;
        border-top: 1px solid var(--border-color);
        margin: var(--space-4) 0;
      }
      
      /* Splitter */
      .splitter {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 6px;
        background: var(--bg-secondary);
        cursor: col-resize;
        flex-shrink: 0;
        border-left: 1px solid var(--border-color);
        border-right: 1px solid var(--border-color);
      }
      
      .splitter:hover {
        background: var(--rax-yellow);
        opacity: 0.5;
      }
      
      /* Empty state */
      .empty-state {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-4);
        color: var(--text-tertiary);
        padding: var(--space-8);
      }
      
      .empty-state-icon {
        font-size: 48px;
        opacity: 0.5;
      }
      
      .empty-state-text {
        font-size: var(--font-sm);
        text-align: center;
        max-width: 300px;
        line-height: 1.5;
      }
      
      /* Conflicts view */
      .conflicts-view {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-5);
      }
      
      .no-conflicts {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }
      
      .no-conflicts-icon {
        font-size: 40px;
        margin-bottom: var(--space-3);
      }
      
      .conflict-card {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-4);
        margin-bottom: var(--space-3);
        border-left: 3px solid var(--warning);
      }
      
      .conflict-card.error {
        border-left-color: var(--error);
      }
      
      .conflict-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-3);
        font-weight: 600;
        color: var(--warning);
      }
      
      .conflict-card.error .conflict-header {
        color: var(--error);
      }
      
      .conflict-files {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-3);
      }
      
      .conflict-file {
        background: var(--bg-primary);
        border-radius: var(--radius-sm);
        padding: var(--space-3);
        border: 1px solid var(--border-color);
      }
      
      .conflict-file-name {
        font-family: ui-monospace, monospace;
        font-size: var(--font-xs);
        color: var(--rax-yellow);
        margin-bottom: var(--space-2);
        font-weight: 600;
      }
      
      .conflict-snippet {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        line-height: 1.4;
        font-style: italic;
      }
      
      .conflict-line {
        font-size: 10px;
        color: var(--text-tertiary);
        margin-top: var(--space-1);
      }
      
      /* Version History */
      .history-view {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-5);
      }
      
      .history-empty {
        text-align: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }
      
      .history-empty-icon {
        font-size: 40px;
        margin-bottom: var(--space-3);
      }
      
      .version-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .version-card {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-3) var(--space-4);
        transition: all var(--transition-fast);
        cursor: pointer;
      }
      
      .version-card:hover {
        border-color: var(--rax-yellow);
      }
      
      .version-card.known-good {
        border-left: 3px solid var(--rax-yellow);
      }
      
      .version-card.selected {
        background: rgba(255, 208, 0, 0.08);
        border-color: var(--rax-yellow);
      }
      
      .version-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-3);
      }
      
      .version-meta {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex: 1;
        min-width: 0;
      }
      
      .version-number {
        font-size: var(--font-xs);
        font-weight: 700;
        color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.15);
        padding: 2px 8px;
        border-radius: var(--radius-full);
        white-space: nowrap;
      }
      
      .version-time {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        white-space: nowrap;
      }
      
      .version-by {
        font-size: 10px;
        color: var(--text-tertiary);
        padding: 1px 6px;
        border-radius: var(--radius-full);
        background: var(--bg-primary);
        white-space: nowrap;
      }
      
      .version-by.agent {
        color: #a78bfa;
        background: rgba(167, 139, 250, 0.1);
      }
      .version-by.revert {
        color: var(--warning);
        background: rgba(245, 158, 11, 0.1);
      }
      
      .known-good-badge {
        font-size: 10px;
        color: var(--rax-yellow);
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 2px;
      }
      
      .version-size {
        font-size: 10px;
        color: var(--text-tertiary);
        white-space: nowrap;
      }
      
      .version-actions {
        display: flex;
        gap: var(--space-1);
        flex-shrink: 0;
      }
      
      /* Diff view */
      .diff-panel {
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      
      .diff-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-2) var(--space-4);
        background: var(--bg-elevated);
        border-bottom: 1px solid var(--border-color);
        flex-shrink: 0;
      }
      
      .diff-toolbar-title {
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .diff-content {
        flex: 1;
        display: flex;
        overflow: hidden;
      }
      
      .diff-side {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-3);
        min-width: 0;
      }
      
      .diff-side + .diff-side {
        border-left: 1px solid var(--border-color);
      }
      
      .diff-side-label {
        font-size: 11px;
        font-weight: 700;
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: var(--space-2);
        padding-bottom: var(--space-2);
        border-bottom: 1px solid var(--border-color);
      }
      
      .diff-text {
        font-family: ui-monospace, "SF Mono", "Cascadia Code", monospace;
        font-size: 12px;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-word;
        color: var(--text-secondary);
      }
      
      .diff-line {
        padding: 0 var(--space-2);
      }
      .diff-line.added {
        background: rgba(16, 185, 129, 0.1);
        color: var(--success);
      }
      .diff-line.removed {
        background: rgba(239, 68, 68, 0.1);
        color: var(--error);
      }
      .diff-line.unchanged {
        opacity: 0.5;
      }
      
      .history-split {
        display: flex;
        flex: 1;
        overflow: hidden;
      }
      
      .history-list-panel {
        width: 380px;
        min-width: 320px;
        border-right: 1px solid var(--border-color);
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      
      .history-list-scroll {
        flex: 1;
        overflow-y: auto;
        padding: var(--space-3);
      }
      
      .history-list-header {
        padding: var(--space-3) var(--space-4);
        border-bottom: 1px solid var(--border-color);
        font-size: var(--font-sm);
        font-weight: 600;
        color: var(--text-primary);
        background: var(--bg-elevated);
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
      }
      
      /* Loading */
      .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--text-tertiary);
        gap: var(--space-3);
      }
      
      .spinner {
        width: 20px;
        height: 20px;
        border: 2px solid var(--border-color);
        border-top-color: var(--rax-yellow);
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
      }
      
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      
      /* Sidebar bottom actions */
      .sidebar-actions {
        padding: var(--space-3);
        border-top: 1px solid var(--border-color);
      }
      
      .refresh-btn {
        width: 100%;
        padding: var(--space-2);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .refresh-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      /* Setup History Section */
      .setup-history {
        margin-top: auto;
        border-top: 1px solid var(--border-color);
      }
      
      .setup-history-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3);
        cursor: pointer;
        transition: background var(--transition-fast);
      }
      
      .setup-history-header:hover {
        background: var(--bg-hover);
      }
      
      .setup-history-title {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        font-weight: 500;
      }
      
      .setup-history-badge {
        font-size: 10px;
        padding: 1px 6px;
        border-radius: var(--radius-full);
        background: rgba(16, 185, 129, 0.15);
        color: var(--success);
        font-weight: 600;
      }
      
      .setup-history-expand {
        font-size: 12px;
        color: var(--text-tertiary);
        transition: transform var(--transition-fast);
      }
      
      .setup-history-expand.open {
        transform: rotate(180deg);
      }
      
      .setup-history-content {
        padding: 0 var(--space-2) var(--space-2);
      }
      
      .setup-history-content .file-item {
        opacity: 0.7;
      }
      
      .reset-setup-btn {
        width: 100%;
        margin-top: var(--space-2);
        padding: var(--space-2);
        background: transparent;
        border: 1px dashed var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-tertiary);
        font-size: var(--font-xs);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .reset-setup-btn:hover {
        border-color: var(--warning);
        color: var(--warning);
        background: rgba(245, 158, 11, 0.05);
      }
    `,
  ];

  // Top-level mode: config files, learned preferences, or quality dashboard
  @state() private mode: "config" | "preferences" | "quality" = "config";

  @state() private files: WorkspaceFile[] = [];
  @state() private selectedFile: string | null = null;
  @state() private editorContent = "";
  @state() private originalContent = "";
  @state() private loading = true;
  @state() private fileLoading = false;
  @state() private saving = false;
  @state() private saveStatus: "none" | "unsaved" | "saving" | "saved" | "error" = "none";
  @state() private fileWarning: string | null = null;
  @state() private activeView: "editor" | "conflicts" | "history" = "editor";
  @state() private conflicts: Conflict[] = [];
  @state() private fileContents: Map<string, string> = new Map();
  @state() private showPreview = true;

  // Version history state
  @state() private versions: FileVersion[] = [];
  @state() private versionsLoading = false;
  @state() private selectedVersionId: string | null = null;
  @state() private selectedVersionDetail: FileVersionDetail | null = null;
  @state() private versionDetailLoading = false;

  // Bootstrap archive state
  @state() private bootstrapCompleted = false;
  @state() private showSetupHistory = false;

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadFiles();
  }

  private mergeWarnings(...warnings: Array<string | null | undefined>): string | null {
    const unique = new Set<string>();
    for (const warning of warnings) {
      if (typeof warning !== "string") continue;
      const normalized = warning.trim();
      if (!normalized) continue;
      unique.add(normalized);
    }
    if (unique.size === 0) return null;
    return Array.from(unique).join(" ");
  }

  private async loadFiles() {
    this.loading = true;
    try {
      const res = await raxApi.listWorkspaceFiles();
      if (res.success && res.data) {
        this.files = res.data.map((f) => ({
          ...f,
          description: FILE_DESCRIPTIONS[f.name]?.description || "Configuration file",
          icon: FILE_DESCRIPTIONS[f.name]?.icon || "📄",
        }));
      }
      // Check if bootstrap is completed
      await this.checkBootstrapStatus();
    } catch (err) {
      console.error("[AgentConfigEditor] Failed to load files:", err);
    } finally {
      this.loading = false;
    }
  }

  private async checkBootstrapStatus() {
    // Check if bootstrap is complete by looking for BOOTSTRAP.md content
    // or checking onboarding status
    try {
      const onboardingRes = await raxApi.getOnboardingStatus();
      if (onboardingRes.success && onboardingRes.data?.status === "completed") {
        this.bootstrapCompleted = true;
        // Mark BOOTSTRAP.md as archived
        const bootstrapFile = this.files.find((f) => f.name === "BOOTSTRAP.md");
        if (bootstrapFile) {
          bootstrapFile.archived = true;
        }
      }
    } catch {
      // If we can't check, assume not completed
    }
  }

  private async resetBootstrap() {
    if (!confirm("This will restart your agent's initial setup. Continue?")) return;
    try {
      // Reset bootstrap by restoring BOOTSTRAP.md to active state
      this.bootstrapCompleted = false;
      const bootstrapFile = this.files.find((f) => f.name === "BOOTSTRAP.md");
      if (bootstrapFile) {
        bootstrapFile.archived = false;
      }
      this.showSetupHistory = false;
      // Select BOOTSTRAP.md to edit
      await this.selectFile("BOOTSTRAP.md");
    } catch (err) {
      console.error("[AgentConfigEditor] Reset bootstrap failed:", err);
    }
  }

  private async selectFile(name: string) {
    if (this.selectedFile === name) return;
    if (this.saveStatus === "unsaved") {
      if (!confirm("You have unsaved changes. Discard?")) return;
    }
    this.selectedFile = name;
    this.fileLoading = true;
    this.saveStatus = "none";
    this.versions = [];
    this.selectedVersionId = null;
    this.selectedVersionDetail = null;
    const selectedFileMeta = this.files.find((file) => file.name === name);
    this.fileWarning = selectedFileMeta?.error || null;
    try {
      const res = (await raxApi.readWorkspaceFile(name)) as {
        success: boolean;
        data?: { name: string; content: string; warning?: string };
        error?: string;
      };
      if (res.success && res.data) {
        this.editorContent = res.data.content;
        this.originalContent = res.data.content;
        this.fileContents.set(name, res.data.content);
        this.fileWarning = this.mergeWarnings(res.data.warning, selectedFileMeta?.error);
      } else {
        const cachedContent = this.fileContents.get(name) || "";
        this.editorContent = cachedContent;
        this.originalContent = cachedContent;
        this.fileWarning = this.mergeWarnings(selectedFileMeta?.error, res.error);
      }
    } catch (err) {
      console.error("[AgentConfigEditor] Failed to read file:", err);
      const cachedContent = this.fileContents.get(name) || "";
      this.editorContent = cachedContent;
      this.originalContent = cachedContent;
      this.fileWarning = this.mergeWarnings(
        selectedFileMeta?.error,
        "Failed to load latest runtime copy.",
      );
    } finally {
      this.fileLoading = false;
    }
    // Pre-load version count in background
    this.loadVersions();
  }

  private async loadVersions() {
    if (!this.selectedFile) return;
    this.versionsLoading = true;
    try {
      const res = await raxApi.getFileVersions(this.selectedFile);
      if (res.success && res.data) {
        this.versions = res.data;
      }
    } catch (err) {
      console.error("[AgentConfigEditor] Failed to load versions:", err);
    } finally {
      this.versionsLoading = false;
    }
  }

  private async selectVersion(versionId: string) {
    if (!this.selectedFile) return;
    if (this.selectedVersionId === versionId) {
      this.selectedVersionId = null;
      this.selectedVersionDetail = null;
      return;
    }
    this.selectedVersionId = versionId;
    this.versionDetailLoading = true;
    try {
      const res = await raxApi.getFileVersion(this.selectedFile, versionId);
      if (res.success && res.data) {
        this.selectedVersionDetail = res.data;
      }
    } catch (err) {
      console.error("[AgentConfigEditor] Failed to load version detail:", err);
    } finally {
      this.versionDetailLoading = false;
    }
  }

  private async revertToVersion(versionId: string) {
    if (!this.selectedFile) return;
    if (!confirm("Revert to this version? Current file will be overwritten.")) return;
    try {
      const res = await raxApi.revertFileVersion(this.selectedFile, versionId);
      if (res.success && res.data) {
        this.editorContent = res.data.content;
        this.originalContent = res.data.content;
        this.saveStatus = "saved";
        this.fileContents.set(this.selectedFile, res.data.content);
        setTimeout(() => {
          if (this.saveStatus === "saved") this.saveStatus = "none";
        }, 2000);
        await this.loadVersions();
      }
    } catch (err) {
      console.error("[AgentConfigEditor] Revert failed:", err);
    }
  }

  private async markKnownGood(versionId: string) {
    if (!this.selectedFile) return;
    try {
      const res = await raxApi.markVersionKnownGood(this.selectedFile, versionId);
      if (res.success) {
        await this.loadVersions();
        // Refresh detail if viewing this version
        if (this.selectedVersionId === versionId && this.selectedVersionDetail) {
          this.selectedVersionDetail = { ...this.selectedVersionDetail, is_known_good: true };
        }
      }
    } catch (err) {
      console.error("[AgentConfigEditor] Mark known good failed:", err);
    }
  }

  private handleEditorInput(e: Event) {
    this.editorContent = (e.target as HTMLTextAreaElement).value;
    this.saveStatus = this.editorContent !== this.originalContent ? "unsaved" : "none";
    if (this.selectedFile) {
      this.fileContents.set(this.selectedFile, this.editorContent);
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "s") {
      e.preventDefault();
      this.saveFile();
    }
    if (e.key === "Tab") {
      e.preventDefault();
      const ta = e.target as HTMLTextAreaElement;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const val = ta.value;
      ta.value = val.substring(0, start) + "  " + val.substring(end);
      ta.selectionStart = ta.selectionEnd = start + 2;
      this.editorContent = ta.value;
      this.saveStatus = "unsaved";
    }
  }

  private async saveFile() {
    if (!this.selectedFile || this.saving) return;
    this.saving = true;
    this.saveStatus = "saving";
    try {
      const res = await raxApi.writeWorkspaceFile(
        this.selectedFile,
        this.editorContent,
        this.originalContent,
      );
      if (res.success) {
        this.originalContent = this.editorContent;
        this.saveStatus = "saved";
        const file = this.files.find((f) => f.name === this.selectedFile);
        if (file) {
          file.exists = true;
          file.size = this.editorContent.length;
          this.requestUpdate();
        }
        setTimeout(() => {
          if (this.saveStatus === "saved") this.saveStatus = "none";
        }, 2000);
        // Refresh versions after save
        this.loadVersions();
      } else {
        this.saveStatus = "error";
      }
    } catch (err) {
      console.error("[AgentConfigEditor] Save failed:", err);
      this.saveStatus = "error";
    } finally {
      this.saving = false;
    }
  }

  private async analyzeConflicts() {
    this.activeView = "conflicts";
    this.conflicts = [];

    const filesToAnalyze = this.files.filter((f) => f.exists);
    for (const file of filesToAnalyze) {
      if (!this.fileContents.has(file.name)) {
        try {
          const res = await raxApi.readWorkspaceFile(file.name);
          if (res.success && res.data) {
            this.fileContents.set(file.name, res.data.content);
          }
        } catch {
          /* skip */
        }
      }
    }

    const matches: Array<{
      file: string;
      line: number;
      snippet: string;
      category: string;
      label: string;
    }> = [];

    for (const [fileName, content] of this.fileContents) {
      const lines = content.split("\n");
      for (let i = 0; i < lines.length; i++) {
        for (const pattern of CONFLICT_PATTERNS) {
          if (pattern.regex.test(lines[i])) {
            matches.push({
              file: fileName,
              line: i + 1,
              snippet: lines[i].trim().substring(0, 120),
              category: pattern.category,
              label: pattern.label,
            });
          }
        }
      }
    }

    const byCategory = new Map<string, typeof matches>();
    for (const m of matches) {
      const arr = byCategory.get(m.category) || [];
      arr.push(m);
      byCategory.set(m.category, arr);
    }

    const conflicts: Conflict[] = [];
    let id = 0;
    for (const [category, categoryMatches] of byCategory) {
      const labels = [...new Set(categoryMatches.map((m) => m.label))];
      if (labels.length < 2) continue;

      const groups = new Map<string, typeof matches>();
      for (const m of categoryMatches) {
        const arr = groups.get(m.label) || [];
        arr.push(m);
        groups.set(m.label, arr);
      }

      const labelEntries = [...groups.entries()];
      for (let i = 0; i < labelEntries.length; i++) {
        for (let j = i + 1; j < labelEntries.length; j++) {
          const [labelA, matchesA] = labelEntries[i];
          const [labelB, matchesB] = labelEntries[j];
          const filesA = new Set(matchesA.map((m) => m.file));
          const filesB = new Set(matchesB.map((m) => m.file));
          const crossFile =
            [...filesA].some((f) => !filesB.has(f)) || [...filesB].some((f) => !filesA.has(f));
          if (!crossFile) continue;

          const a = matchesA[0];
          const b = matchesB[0];
          conflicts.push({
            id: `conflict-${id++}`,
            severity: category === "restriction" ? "error" : "warning",
            message: `${category}: "${labelA}" vs "${labelB}"`,
            fileA: a.file,
            lineA: a.line,
            snippetA: a.snippet,
            fileB: b.file,
            lineB: b.line,
            snippetB: b.snippet,
          });
        }
      }
    }

    this.conflicts = conflicts;
  }

  private async showHistory() {
    this.activeView = "history";
    if (this.selectedFile && this.versions.length === 0) {
      await this.loadVersions();
    }
  }

  private renderMarkdownPreview(content: string): string {
    return marked.parse(content, { async: false, breaks: true }) as string;
  }

  private getSaveStatusText(): string {
    switch (this.saveStatus) {
      case "unsaved":
        return "● Unsaved changes";
      case "saving":
        return "⏳ Saving...";
      case "saved":
        return "✓ Saved";
      case "error":
        return "✕ Save failed";
      default:
        return "";
    }
  }

  private formatTime(isoStr: string): string {
    const d = new Date(isoStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  private formatBytes(len: number): string {
    if (len < 1024) return `${len}B`;
    return `${(len / 1024).toFixed(1)}KB`;
  }

  private computeSimpleDiff(
    oldText: string,
    newText: string,
  ): Array<{ type: "added" | "removed" | "unchanged"; text: string }> {
    const oldLines = oldText.split("\n");
    const newLines = newText.split("\n");
    const result: Array<{ type: "added" | "removed" | "unchanged"; text: string }> = [];

    // Simple line-by-line diff using LCS-like approach
    const maxLen = Math.max(oldLines.length, newLines.length);
    let oi = 0,
      ni = 0;
    while (oi < oldLines.length || ni < newLines.length) {
      if (oi < oldLines.length && ni < newLines.length && oldLines[oi] === newLines[ni]) {
        result.push({ type: "unchanged", text: oldLines[oi] });
        oi++;
        ni++;
      } else {
        // Look ahead to find next match
        let foundOld = -1,
          foundNew = -1;
        for (let look = 1; look < Math.min(10, maxLen); look++) {
          if (
            foundNew === -1 &&
            ni + look < newLines.length &&
            oldLines[oi] === newLines[ni + look]
          ) {
            foundNew = ni + look;
          }
          if (
            foundOld === -1 &&
            oi + look < oldLines.length &&
            oldLines[oi + look] === newLines[ni]
          ) {
            foundOld = oi + look;
          }
          if (foundOld !== -1 || foundNew !== -1) break;
        }

        if (foundOld !== -1 && (foundNew === -1 || foundOld - oi <= foundNew - ni)) {
          // Lines removed from old
          while (oi < foundOld) {
            result.push({ type: "removed", text: oldLines[oi++] });
          }
        } else if (foundNew !== -1) {
          // Lines added in new
          while (ni < foundNew) {
            result.push({ type: "added", text: newLines[ni++] });
          }
        } else {
          // No match found nearby — show as remove + add
          if (oi < oldLines.length) {
            result.push({ type: "removed", text: oldLines[oi++] });
          }
          if (ni < newLines.length) {
            result.push({ type: "added", text: newLines[ni++] });
          }
        }
      }
    }
    return result;
  }

  private getSortedVersions(): FileVersion[] {
    // Known good versions float to top, then newest first
    const knownGood = this.versions.filter((v) => v.is_known_good);
    const rest = this.versions.filter((v) => !v.is_known_good);
    return [...knownGood, ...rest];
  }

  override render() {
    return html`
      <div class="mode-tabs">
        <button
          class="mode-tab ${this.mode === "config" ? "active" : ""}"
          @click=${() => (this.mode = "config")}
        >
          Config Files
        </button>
        <button
          class="mode-tab ${this.mode === "preferences" ? "active" : ""}"
          @click=${() => (this.mode = "preferences")}
        >
          Learned Preferences
        </button>
        <button
          class="mode-tab ${this.mode === "quality" ? "active" : ""}"
          @click=${() => (this.mode = "quality")}
        >
          Quality
        </button>
      </div>
      ${
        this.mode === "config"
          ? this.renderConfigEditor()
          : this.mode === "preferences"
            ? html`
                <rax-learning-ledger></rax-learning-ledger>
              `
            : html`
                <rax-quality-dashboard></rax-quality-dashboard>
              `
      }
    `;
  }

  private renderConfigEditor() {
    if (this.loading) {
      return html`
        <div class="loading">
          <div class="spinner"></div>
          Loading workspace files...
        </div>
      `;
    }

    return html`
      <div class="editor-container">
        <div class="file-sidebar">
          <div class="sidebar-header">
            <div class="sidebar-title">⚡ Agent Config</div>
          </div>
          <div class="file-list">
            ${this.files
              .filter((f) => !f.archived)
              .map(
                (file) => html`
              <div
                class="file-item ${this.selectedFile === file.name ? "active" : ""} ${!file.exists ? "not-exists" : ""}"
                @click=${() => this.selectFile(file.name)}
              >
                <span class="file-icon">${file.icon}</span>
                <div class="file-info">
                  <div class="file-name">${file.name}</div>
                  <div class="file-desc">${file.error ? file.error : file.description}</div>
                </div>
                <span class="file-status ${file.exists ? "exists" : "missing"}">
                  ${file.exists ? "✓" : file.error ? "⚠" : "—"}
                </span>
              </div>
            `,
              )}
          </div>
          ${
            this.files.some((f) => f.archived)
              ? html`
            <div class="setup-history">
              <div class="setup-history-header" @click=${() => (this.showSetupHistory = !this.showSetupHistory)}>
                <div class="setup-history-title">
                  <span>📦 Setup History</span>
                  <span class="setup-history-badge">✓ Completed</span>
                </div>
                <span class="setup-history-expand ${this.showSetupHistory ? "open" : ""}">▼</span>
              </div>
              ${
                this.showSetupHistory
                  ? html`
                <div class="setup-history-content">
                  ${this.files
                    .filter((f) => f.archived)
                    .map(
                      (file) => html`
                    <div
                      class="file-item ${this.selectedFile === file.name ? "active" : ""}"
                      @click=${() => this.selectFile(file.name)}
                    >
                      <span class="file-icon">${file.icon}</span>
                      <div class="file-info">
                        <div class="file-name">${file.name}</div>
                        <div class="file-desc">Initial setup (completed)</div>
                      </div>
                      <span class="file-status exists">✓</span>
                    </div>
                  `,
                    )}
                  <button class="reset-setup-btn" @click=${this.resetBootstrap}>
                    🔄 Reset Setup
                  </button>
                </div>
              `
                  : ""
              }
            </div>
          `
              : ""
          }
          <div class="sidebar-actions">
            <button class="refresh-btn" @click=${this.loadFiles}>🔄 Refresh Files</button>
          </div>
        </div>

        <div class="main-content">
          <div class="tab-bar">
            <button
              class="tab ${this.activeView === "editor" ? "active" : ""}"
              @click=${() => {
                this.activeView = "editor";
              }}
            >✏️ Editor</button>
            <button
              class="tab ${this.activeView === "history" ? "active" : ""}"
              @click=${this.showHistory}
            >
              🕐 History
              ${this.versions.length > 0 ? html`<span class="badge-muted">${this.versions.length}</span>` : ""}
            </button>
            <button
              class="tab ${this.activeView === "conflicts" ? "active" : ""}"
              @click=${this.analyzeConflicts}
            >
              ⚠️ Conflicts
              ${this.conflicts.length > 0 ? html`<span class="badge">${this.conflicts.length}</span>` : ""}
            </button>
          </div>

          ${
            this.activeView === "editor"
              ? this.renderEditorView()
              : this.activeView === "history"
                ? this.renderHistoryView()
                : this.renderConflictsView()
          }
        </div>
      </div>
    `;
  }

  private renderEditorView() {
    if (!this.selectedFile) {
      return html`
        <div class="empty-state">
          <div class="empty-state-icon">📂</div>
          <div class="empty-state-text">
            Select a file from the sidebar to view and edit your agent's configuration.
            <br /><br />
            These files control your agent's personality, behavior, memory, and tools.
          </div>
        </div>
      `;
    }

    if (this.fileLoading) {
      return html`<div class="loading"><div class="spinner"></div> Loading ${this.selectedFile}...</div>`;
    }

    return html`
      <div class="editor-toolbar">
        <span class="editor-filename">${this.selectedFile}</span>
        <div class="editor-actions">
          <span class="save-indicator ${this.saveStatus}">${this.getSaveStatusText()}</span>
          <button
            class="btn btn-secondary"
            @click=${() => {
              this.showPreview = !this.showPreview;
            }}
          >${this.showPreview ? "👁️ Hide Preview" : "👁️ Show Preview"}</button>
          <button
            class="btn btn-primary"
            ?disabled=${this.saveStatus !== "unsaved"}
            @click=${this.saveFile}
          >💾 Save (⌘S)</button>
        </div>
      </div>
      ${this.fileWarning ? html`<div class="file-warning">⚠️ ${this.fileWarning}</div>` : ""}
      <div class="editor-area">
        <div class="editor-pane">
          <textarea
            class="code-editor"
            .value=${this.editorContent}
            @input=${this.handleEditorInput}
            @keydown=${this.handleKeyDown}
            placeholder="Start typing your configuration..."
            spellcheck="false"
          ></textarea>
        </div>
        ${
          this.showPreview
            ? html`
          <div class="splitter"></div>
          <div class="preview-pane">
            ${unsafeHTML(this.renderMarkdownPreview(this.editorContent))}
          </div>
        `
            : ""
        }
      </div>
    `;
  }

  private renderHistoryView() {
    if (!this.selectedFile) {
      return html`
        <div class="empty-state">
          <div class="empty-state-icon">🕐</div>
          <div class="empty-state-text">Select a file from the sidebar to view its version history.</div>
        </div>
      `;
    }

    if (this.versionsLoading && this.versions.length === 0) {
      return html`
        <div class="loading">
          <div class="spinner"></div>
          Loading version history...
        </div>
      `;
    }

    if (this.versions.length === 0) {
      return html`
        <div class="history-view">
          <div class="history-empty">
            <div class="history-empty-icon">📜</div>
            <p>No version history yet for <strong>${this.selectedFile}</strong>.</p>
            <p style="font-size: var(--font-xs); margin-top: var(--space-2); color: var(--text-tertiary);">
              Versions are created automatically each time you save.
            </p>
          </div>
        </div>
      `;
    }

    const sortedVersions = this.getSortedVersions();

    return html`
      <div class="history-split">
        <div class="history-list-panel">
          <div class="history-list-header">
            <span>📜 ${this.selectedFile}</span>
            <span style="font-size: 11px; color: var(--text-tertiary);">${this.versions.length} version${this.versions.length !== 1 ? "s" : ""}</span>
          </div>
          <div class="history-list-scroll">
            <div class="version-list">
              ${sortedVersions.map(
                (v) => html`
                <div
                  class="version-card ${v.is_known_good ? "known-good" : ""} ${this.selectedVersionId === v.id ? "selected" : ""}"
                  @click=${() => this.selectVersion(v.id)}
                >
                  <div class="version-header">
                    <div class="version-meta">
                      <span class="version-number">v${v.version_number}</span>
                      <span class="version-time">${this.formatTime(v.created_at)}</span>
                      <span class="version-by ${v.created_by === "agent" ? "agent" : v.created_by === "revert" ? "revert" : ""}">${v.created_by}</span>
                      ${
                        v.is_known_good
                          ? html`
                              <span class="known-good-badge">⭐ known good</span>
                            `
                          : ""
                      }
                    </div>
                    <span class="version-size">${this.formatBytes(v.content_length)}</span>
                  </div>
                  <div class="version-actions" style="margin-top: 6px;">
                    <button class="btn btn-sm btn-secondary" @click=${(e: Event) => {
                      e.stopPropagation();
                      this.revertToVersion(v.id);
                    }}>↩ Revert</button>
                    ${
                      !v.is_known_good
                        ? html`
                      <button class="btn btn-sm btn-star" @click=${(e: Event) => {
                        e.stopPropagation();
                        this.markKnownGood(v.id);
                      }}>⭐ Mark Good</button>
                    `
                        : ""
                    }
                  </div>
                </div>
              `,
              )}
            </div>
          </div>
        </div>

        <div class="diff-panel">
          ${
            this.selectedVersionId
              ? this.renderDiffPanel()
              : html`
                  <div class="empty-state">
                    <div class="empty-state-icon">👈</div>
                    <div class="empty-state-text">Click a version to see what changed.</div>
                  </div>
                `
          }
        </div>
      </div>
    `;
  }

  private renderDiffPanel() {
    if (this.versionDetailLoading) {
      return html`
        <div class="loading">
          <div class="spinner"></div>
          Loading version...
        </div>
      `;
    }

    if (!this.selectedVersionDetail) {
      return html`
        <div class="empty-state"><div class="empty-state-text">Could not load version.</div></div>
      `;
    }

    const v = this.selectedVersionDetail;
    const hasOld = v.previous_content !== null && v.previous_content !== undefined;
    const oldText = hasOld ? v.previous_content! : "";
    const newText = v.content;

    if (!hasOld) {
      // No previous content — just show the saved content
      return html`
        <div style="display: flex; flex-direction: column; height: 100%;">
          <div class="diff-toolbar">
            <div class="diff-toolbar-title">v${v.version_number} — ${this.formatTime(v.created_at)}</div>
            <div style="display: flex; gap: 6px; align-items: center;">
              ${
                v.is_known_good
                  ? html`
                      <span class="known-good-badge">⭐ Known Good</span>
                    `
                  : ""
              }
              <span class="version-by ${v.created_by === "agent" ? "agent" : v.created_by === "revert" ? "revert" : ""}">${v.created_by}</span>
            </div>
          </div>
          <div style="flex: 1; overflow-y: auto; padding: var(--space-4);">
            <div class="diff-side-label">Saved Content (no previous version to diff)</div>
            <div class="diff-text">${newText}</div>
          </div>
        </div>
      `;
    }

    const diffLines = this.computeSimpleDiff(oldText, newText);
    const added = diffLines.filter((l) => l.type === "added").length;
    const removed = diffLines.filter((l) => l.type === "removed").length;

    return html`
      <div style="display: flex; flex-direction: column; height: 100%;">
        <div class="diff-toolbar">
          <div class="diff-toolbar-title">
            v${v.version_number} — ${this.formatTime(v.created_at)}
            <span style="font-weight: 400; font-size: 11px; margin-left: 8px; color: var(--text-tertiary);">
              <span style="color: var(--success);">+${added}</span>
              <span style="color: var(--error); margin-left: 4px;">−${removed}</span>
            </span>
          </div>
          <div style="display: flex; gap: 6px; align-items: center;">
            ${
              v.is_known_good
                ? html`
                    <span class="known-good-badge">⭐ Known Good</span>
                  `
                : ""
            }
            <span class="version-by ${v.created_by === "agent" ? "agent" : v.created_by === "revert" ? "revert" : ""}">${v.created_by}</span>
          </div>
        </div>
        <div class="diff-content">
          <div class="diff-side">
            <div class="diff-side-label">Before</div>
            <div class="diff-text">
              ${diffLines
                .filter((l) => l.type !== "added")
                .map(
                  (l) => html`
                <div class="diff-line ${l.type}">${l.text || " "}</div>
              `,
                )}
            </div>
          </div>
          <div class="diff-side">
            <div class="diff-side-label">After</div>
            <div class="diff-text">
              ${diffLines
                .filter((l) => l.type !== "removed")
                .map(
                  (l) => html`
                <div class="diff-line ${l.type}">${l.text || " "}</div>
              `,
                )}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderConflictsView() {
    if (this.conflicts.length === 0) {
      return html`
        <div class="conflicts-view">
          <div class="no-conflicts">
            <div class="no-conflicts-icon">✅</div>
            <p>No conflicts detected across your configuration files.</p>
            <p style="font-size: var(--font-xs); margin-top: var(--space-2)">
              The analyzer checks for contradictory instructions about tone, verbosity, autonomy, tool
              usage, and more.
            </p>
          </div>
        </div>
      `;
    }

    return html`
      <div class="conflicts-view">
        <p style="color: var(--text-secondary); margin-bottom: var(--space-4); font-size: var(--font-sm);">
          Found <strong style="color: var(--warning)">${this.conflicts.length}</strong> potential conflict${this.conflicts.length !== 1 ? "s" : ""} across your config files:
        </p>
        ${this.conflicts.map(
          (c) => html`
          <div class="conflict-card ${c.severity}">
            <div class="conflict-header">
              ${c.severity === "error" ? "🔴" : "🟡"} ${c.message}
            </div>
            <div class="conflict-files">
              <div class="conflict-file">
                <div class="conflict-file-name">${c.fileA}</div>
                <div class="conflict-snippet">"${c.snippetA}"</div>
                ${c.lineA ? html`<div class="conflict-line">Line ${c.lineA}</div>` : ""}
              </div>
              <div class="conflict-file">
                <div class="conflict-file-name">${c.fileB}</div>
                <div class="conflict-snippet">"${c.snippetB}"</div>
                ${c.lineB ? html`<div class="conflict-line">Line ${c.lineB}</div>` : ""}
              </div>
            </div>
          </div>
        `,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-agent-config-editor": RaxAgentConfigEditor;
  }
}
